// Shader compile and link, and the uniform map (the plan's A.2
// `gl/program.ts`).
//
// A program is a `GpuResource`: a lost context destroys it and the restore
// compiles it again from the sources it kept. Every uniform is read once
// from `getActiveUniform`, so a caller sets by name and a name the compiler
// removed costs one map lookup and returns.

import type { GL, GpuResource } from './context';
import { cornerArray, type CornerArray } from './corner';
import type { Pipeline } from '../gfx/types';

/** Cuts the reported line out of the source, so a compile error is readable. */
function excerpt(source: string, log: string): string {
  // The driver reports `ERROR: 0:123: ...`. Take the first line number.
  const m = /:(\d+):/.exec(log);
  if (!m) return '';
  const line = Number(m[1]);
  const lines = source.split('\n');
  const from = Math.max(0, line - 3);
  const to = Math.min(lines.length, line + 2);
  const out: string[] = [];
  for (let i = from; i < to; i++) {
    out.push(`${i + 1 === line ? '>' : ' '} ${String(i + 1).padStart(4)} | ${lines[i]}`);
  }
  return out.join('\n');
}

export class Program implements GpuResource, Pipeline {
  program: WebGLProgram | null = null;
  private uniforms = new Map<string, WebGLUniformLocation>();
  private linkChecked = false;
  /**
   * The shared four-corner vertex array (R553), built on the first
   * `drawQuad`.
   *
   * It is built lazily and not in the constructor, because a program that
   * only ever draws instances or a mesh must not make one. `cornerArray`
   * registers the corner BUFFER before the array, so a restore always
   * rebuilds the buffer first.
   */
  private quad: CornerArray | null = null;

  constructor(
    private readonly glc: GL,
    readonly name: string,
    private readonly vertexSource: string,
    private readonly fragmentSource: string,
  ) {
    glc.register(this);
  }

  create(gl: WebGL2RenderingContext): void {
    this.destroy(gl);
    const vs = this.compile(gl, gl.VERTEX_SHADER, this.vertexSource);
    const fs = this.compile(gl, gl.FRAGMENT_SHADER, this.fragmentSource);
    const p = gl.createProgram();
    if (!p) throw new Error(`fast: cannot create program ${this.name}`);
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    // The link status is a synchronous stall. With
    // KHR_parallel_shader_compile the driver links in the background and the
    // first `use()` pays for the wait instead of the boot.
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    this.program = p;
    this.linkChecked = !gl.getExtension('KHR_parallel_shader_compile');
    if (this.linkChecked) this.finishLink(gl);
  }

  private finishLink(gl: WebGL2RenderingContext) {
    const p = this.program;
    if (!p) return;
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(p) ?? '';
      throw new Error(`fast: link failed for ${this.name}\n${log}`);
    }
    this.uniforms.clear();
    const count = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS) as number;
    for (let i = 0; i < count; i++) {
      const info = gl.getActiveUniform(p, i);
      if (!info) continue;
      const loc = gl.getUniformLocation(p, info.name);
      if (loc) this.uniforms.set(info.name, loc);
      // An array reports as `uPalette[0]`; store the bare name too, so a
      // caller writes `uniform4fv('uPalette', …)`.
      const bracket = info.name.indexOf('[');
      if (bracket > 0 && loc) this.uniforms.set(info.name.slice(0, bracket), loc);
    }
  }

  private compile(gl: WebGL2RenderingContext, kind: number, source: string): WebGLShader {
    const s = gl.createShader(kind);
    if (!s) throw new Error(`fast: cannot create shader for ${this.name}`);
    gl.shaderSource(s, source);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(s) ?? '';
      gl.deleteShader(s);
      const kindName = kind === gl.VERTEX_SHADER ? 'vertex' : 'fragment';
      throw new Error(`fast: ${kindName} shader failed for ${this.name}\n${log}\n${excerpt(source, log)}`);
    }
    return s;
  }

  destroy(gl: WebGL2RenderingContext): void {
    if (this.program) gl.deleteProgram(this.program);
    this.program = null;
    this.uniforms.clear();
    this.linkChecked = false;
  }

  /** Binds the program. Returns false when the context is lost. */
  use(): boolean {
    if (!this.program || this.glc.lost) return false;
    if (!this.linkChecked) {
      this.linkChecked = true;
      this.finishLink(this.glc.gl);
    }
    this.glc.gl.useProgram(this.program);
    return true;
  }

  has(name: string): boolean { return this.uniforms.has(name); }

  // The setters. Each returns at once for a uniform the compiler removed,
  // so a shared uniform contract costs nothing in a shader that ignores it.
  uniform1i(name: string, v: number) {
    const l = this.uniforms.get(name); if (l) this.glc.gl.uniform1i(l, v);
  }
  uniform1f(name: string, v: number) {
    const l = this.uniforms.get(name); if (l) this.glc.gl.uniform1f(l, v);
  }
  uniform2f(name: string, x: number, y: number) {
    const l = this.uniforms.get(name); if (l) this.glc.gl.uniform2f(l, x, y);
  }
  uniform2i(name: string, x: number, y: number) {
    const l = this.uniforms.get(name); if (l) this.glc.gl.uniform2i(l, x, y);
  }
  uniform3f(name: string, x: number, y: number, z: number) {
    const l = this.uniforms.get(name); if (l) this.glc.gl.uniform3f(l, x, y, z);
  }
  uniform4f(name: string, x: number, y: number, z: number, w: number) {
    const l = this.uniforms.get(name); if (l) this.glc.gl.uniform4f(l, x, y, z, w);
  }
  uniform1fv(name: string, v: Float32Array) {
    const l = this.uniforms.get(name); if (l) this.glc.gl.uniform1fv(l, v);
  }
  uniform3fv(name: string, v: Float32Array) {
    const l = this.uniforms.get(name); if (l) this.glc.gl.uniform3fv(l, v);
  }
  uniform4fv(name: string, v: Float32Array) {
    const l = this.uniforms.get(name); if (l) this.glc.gl.uniform4fv(l, v);
  }
  uniformMatrix3fv(name: string, v: Float32Array) {
    const l = this.uniforms.get(name); if (l) this.glc.gl.uniformMatrix3fv(l, false, v);
  }
  /** Points a sampler at a texture unit. */
  sampler(name: string, unit: number) { this.uniform1i(name, unit); }

  /**
   * Draws the shared four-corner strip once.
   *
   * The corner array, and not the default one: a draw whose attribute 0 is
   * not a per-vertex array is emulated by Firefox on a Mac (R553).
   */
  drawQuad(): void {
    if (this.glc.lost) return;
    const quad = this.quad ?? (this.quad = cornerArray(this.glc));
    const gl = this.glc.gl;
    quad.bind(gl);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.bindVertexArray(null);
    this.glc.drawCalls++;
    this.glc.instances++;
  }

  /** Drops the program. The context destroys it and forgets it. */
  dispose(): void { this.glc.unregister(this); }
}
